/**
 * AwsS3DataExportIntegrationPage - AWS S3 data export integration.
 * Sections: S3 bucket config (create destination) + schedule export job +
 * recent exports log. GREEN-only accent palette over zinc neutrals.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCreateDestination,
	useCreateJob,
	useDestinationsList,
	useJobsList,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const LABEL = "text-xs font-medium text-zinc-700";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_BASE =
	"inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium";
const DATASETS = ["events", "conversions", "clicks", "impressions"] as const;

function statusBadge(status: string): string {
	if (status === "done" || status === "active") {
		return `${BADGE_BASE} border-green-200 bg-green-50 text-green-800`;
	}
	if (status === "failed") {
		return `${BADGE_BASE} border-zinc-300 bg-zinc-100 text-zinc-800`;
	}
	// queued / running / unknown
	return `${BADGE_BASE} border-zinc-200 bg-white text-zinc-600`;
}

function fmtTs(secs: number): string {
	if (!Number.isFinite(secs) || secs <= 0) return "-";
	const d = new Date(secs * 1000);
	return d.toISOString().replace("T", " ").slice(0, 19);
}

export function AwsS3DataExportIntegrationPage() {
	const destinationsQuery = useDestinationsList();
	const jobsQuery = useJobsList();
	const createDest = useCreateDestination();
	const createJob = useCreateJob();

	// S3 config form state
	const [bucket, setBucket] = useState("");
	const [prefix, setPrefix] = useState("");
	const [region, setRegion] = useState("us-east-1");
	const [accessKeyId, setAccessKeyId] = useState("");
	const [secretKey, setSecretKey] = useState("");

	// Schedule export form state
	const [destinationId, setDestinationId] = useState("");
	const [dataset, setDataset] = useState<string>("events");
	const [idempotencyKey, setIdempotencyKey] = useState("");
	const [rangeStart, setRangeStart] = useState("");
	const [rangeEnd, setRangeEnd] = useState("");

	const destinations = destinationsQuery.data?.items ?? [];
	const jobs = jobsQuery.data?.items ?? [];
	const recent = useMemo(() => jobs.slice(0, 20), [jobs]);

	const handleCreateDestination = (e: FormEvent) => {
		e.preventDefault();
		if (!bucket.trim() || !region.trim() || !accessKeyId.trim() || !secretKey) {
			return;
		}
		createDest.mutate(
			{
				bucket: bucket.trim(),
				prefix: prefix.trim(),
				region: region.trim(),
				access_key_id: accessKeyId.trim(),
				secret_key: secretKey,
			},
			{
				onSuccess: () => {
					setBucket("");
					setPrefix("");
					setAccessKeyId("");
					setSecretKey("");
				},
			},
		);
	};

	const handleScheduleJob = (e: FormEvent) => {
		e.preventDefault();
		const rs = Number(rangeStart);
		const re = Number(rangeEnd);
		if (
			!destinationId ||
			!idempotencyKey.trim() ||
			!Number.isFinite(rs) ||
			!Number.isFinite(re) ||
			re <= rs
		) {
			return;
		}
		createJob.mutate(
			{
				destination_id: destinationId,
				idempotency_key: idempotencyKey.trim(),
				dataset,
				range_start: rs,
				range_end: re,
			},
			{
				onSuccess: () => {
					setIdempotencyKey("");
					setRangeStart("");
					setRangeEnd("");
				},
			},
		);
	};

	return (
		<main
			data-testid="aws-s3-data-export-integration-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					aws-s3-data-export-integration
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					AWS S3 Data Export
				</h1>
				<p className="text-sm text-zinc-600">
					Configure an S3 bucket destination, schedule dataset exports, and
					review recent runs.
				</p>
			</header>

			<section className={CARD} aria-label="S3 bucket configuration">
				<h2 className="text-base font-semibold text-zinc-900">
					S3 bucket configuration
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Add a destination. The secret is never stored in plain text - only a
					reference is kept server-side.
				</p>
				<form
					onSubmit={handleCreateDestination}
					className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="aws-s3-data-export-integration-config-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Bucket</span>
						<input
							type="text"
							className={INPUT}
							value={bucket}
							onChange={(e) => setBucket(e.currentTarget.value)}
							placeholder="my-exports-bucket"
							disabled={createDest.isPending}
							data-testid="aws-s3-data-export-integration-bucket"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Region</span>
						<input
							type="text"
							className={INPUT}
							value={region}
							onChange={(e) => setRegion(e.currentTarget.value)}
							placeholder="us-east-1"
							disabled={createDest.isPending}
							data-testid="aws-s3-data-export-integration-region"
						/>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>Prefix (optional)</span>
						<input
							type="text"
							className={INPUT}
							value={prefix}
							onChange={(e) => setPrefix(e.currentTarget.value)}
							placeholder="exports/2026/"
							disabled={createDest.isPending}
							data-testid="aws-s3-data-export-integration-prefix"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Access key ID</span>
						<input
							type="text"
							className={`${INPUT} font-mono`}
							value={accessKeyId}
							onChange={(e) => setAccessKeyId(e.currentTarget.value)}
							placeholder="AKIA................"
							autoComplete="off"
							spellCheck={false}
							disabled={createDest.isPending}
							data-testid="aws-s3-data-export-integration-access-key"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Secret access key</span>
						<input
							type="password"
							className={`${INPUT} font-mono`}
							value={secretKey}
							onChange={(e) => setSecretKey(e.currentTarget.value)}
							placeholder="********"
							autoComplete="new-password"
							spellCheck={false}
							disabled={createDest.isPending}
							data-testid="aws-s3-data-export-integration-secret-key"
						/>
					</label>
					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN}
							disabled={
								createDest.isPending ||
								!bucket.trim() ||
								!region.trim() ||
								!accessKeyId.trim() ||
								!secretKey
							}
							data-testid="aws-s3-data-export-integration-save-config"
						>
							{createDest.isPending ? "Saving" : "Save destination"}
						</button>
					</div>
				</form>
				{createDest.error ? (
					<p
						className="mt-3 text-xs text-zinc-800"
						role="alert"
						data-testid="aws-s3-data-export-integration-config-error"
					>
						{createDest.error.message}
					</p>
				) : null}

				<div className="mt-6">
					<h3 className="text-sm font-semibold text-zinc-900">
						Saved destinations
					</h3>
					{destinationsQuery.isLoading ? (
						<p
							className="mt-2 text-sm text-zinc-500"
							data-testid="aws-s3-data-export-integration-dest-loading"
						>
							Loading destinations
						</p>
					) : destinationsQuery.error ? (
						<p
							className="mt-2 text-sm text-zinc-800"
							role="alert"
							data-testid="aws-s3-data-export-integration-dest-error"
						>
							{destinationsQuery.error.message}
						</p>
					) : destinations.length === 0 ? (
						<p
							className="mt-2 text-sm text-zinc-500"
							data-testid="aws-s3-data-export-integration-dest-empty"
						>
							No destinations configured.
						</p>
					) : (
						<ul
							className="mt-2 divide-y divide-zinc-200"
							data-testid="aws-s3-data-export-integration-dest-list"
						>
							{destinations.map((d) => (
								<li
									key={d.id}
									className="flex flex-wrap items-center justify-between gap-3 py-2"
									data-testid={`aws-s3-data-export-integration-dest-${d.id}`}
								>
									<div className="flex flex-col">
										<span className="text-sm font-medium text-zinc-900">
											{d.bucket}
											{d.prefix ? (
												<span className="text-zinc-500">/{d.prefix}</span>
											) : null}
										</span>
										<span className="text-xs text-zinc-500">
											{d.region} . key ****{d.access_key_id_last4}
										</span>
									</div>
									<span className={statusBadge(d.status)}>{d.status}</span>
								</li>
							))}
						</ul>
					)}
				</div>
			</section>

			<section className={CARD} aria-label="Schedule export">
				<h2 className="text-base font-semibold text-zinc-900">
					Schedule an export
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Pick a destination, dataset, and a unix-second time range. The
					idempotency key prevents duplicate submissions.
				</p>
				<form
					onSubmit={handleScheduleJob}
					className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="aws-s3-data-export-integration-schedule-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Destination</span>
						<select
							className={SELECT}
							value={destinationId}
							onChange={(e) => setDestinationId(e.currentTarget.value)}
							disabled={createJob.isPending || destinations.length === 0}
							data-testid="aws-s3-data-export-integration-job-destination"
						>
							<option value="">Select destination</option>
							{destinations.map((d) => (
								<option key={d.id} value={d.id}>
									{d.bucket}
									{d.prefix ? `/${d.prefix}` : ""} ({d.region})
								</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Dataset</span>
						<select
							className={SELECT}
							value={dataset}
							onChange={(e) => setDataset(e.currentTarget.value)}
							disabled={createJob.isPending}
							data-testid="aws-s3-data-export-integration-job-dataset"
						>
							{DATASETS.map((d) => (
								<option key={d} value={d}>
									{d}
								</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>Idempotency key</span>
						<input
							type="text"
							className={INPUT}
							value={idempotencyKey}
							onChange={(e) => setIdempotencyKey(e.currentTarget.value)}
							placeholder="daily-events-2026-04-21"
							disabled={createJob.isPending}
							data-testid="aws-s3-data-export-integration-job-idem"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Range start (unix seconds)</span>
						<input
							type="number"
							inputMode="numeric"
							className={INPUT}
							value={rangeStart}
							onChange={(e) => setRangeStart(e.currentTarget.value)}
							placeholder="1761000000"
							disabled={createJob.isPending}
							data-testid="aws-s3-data-export-integration-job-range-start"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Range end (unix seconds)</span>
						<input
							type="number"
							inputMode="numeric"
							className={INPUT}
							value={rangeEnd}
							onChange={(e) => setRangeEnd(e.currentTarget.value)}
							placeholder="1761086400"
							disabled={createJob.isPending}
							data-testid="aws-s3-data-export-integration-job-range-end"
						/>
					</label>
					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN}
							disabled={
								createJob.isPending ||
								!destinationId ||
								!idempotencyKey.trim() ||
								!rangeStart ||
								!rangeEnd
							}
							data-testid="aws-s3-data-export-integration-schedule-submit"
						>
							{createJob.isPending ? "Scheduling" : "Schedule export"}
						</button>
					</div>
				</form>
				{createJob.error ? (
					<p
						className="mt-3 text-xs text-zinc-800"
						role="alert"
						data-testid="aws-s3-data-export-integration-job-error"
					>
						{createJob.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Recent exports">
				<h2 className="text-base font-semibold text-zinc-900">
					Recent exports
				</h2>
				{jobsQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="aws-s3-data-export-integration-jobs-loading"
					>
						Loading
					</p>
				) : jobsQuery.error ? (
					<p
						className="mt-3 text-sm text-zinc-800"
						role="alert"
						data-testid="aws-s3-data-export-integration-jobs-error"
					>
						{jobsQuery.error.message}
					</p>
				) : recent.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="aws-s3-data-export-integration-jobs-empty"
					>
						No exports yet.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table
							className="min-w-full text-left text-sm"
							data-testid="aws-s3-data-export-integration-jobs-table"
						>
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
									<th className="py-2 pr-4">Created</th>
									<th className="py-2 pr-4">Dataset</th>
									<th className="py-2 pr-4">Range</th>
									<th className="py-2 pr-4">Rows</th>
									<th className="py-2 pr-4">Status</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{recent.map((j) => (
									<tr
										key={j.id}
										data-testid={`aws-s3-data-export-integration-job-${j.id}`}
									>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-700">
											{fmtTs(j.created_at)}
										</td>
										<td className="py-2 pr-4 text-zinc-800">{j.dataset}</td>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-600">
											{fmtTs(j.range_start)} {"->"} {fmtTs(j.range_end)}
										</td>
										<td className="py-2 pr-4 text-zinc-800">
											{j.rows_exported}
										</td>
										<td className="py-2 pr-4">
											<span className={statusBadge(j.status)}>{j.status}</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AwsS3DataExportIntegrationPage;
